import {
  type MutateOptions,
  useMutation,
  type UseMutationOptions,
} from '@tanstack/react-query';
import { gql } from 'graphql-request';
import { useCallback } from 'react';
import { gqlClient } from '@/lib';
import type { RemoveEventInput, Exact } from '@/types/gql.generated';
import type { QueryError } from '@/utils/errors';
import type {
  RemoveEventMutation,
  RemoveEventMutationVariables,
} from './useRemoveEvent.generated';

const query = gql`
  mutation RemoveEvent($input: RemoveEventInput!) {
    removeEvent(input: $input)
  }
`;

export const useRemoveEvent = (
  { experienceId }: { experienceId: string },
  options?: Pick<
    UseMutationOptions<
      RemoveEventMutation,
      QueryError,
      Exact<{ input: RemoveEventInput }>
    >,
    'onSuccess' | 'onError' | 'onSettled'
  >
) => {
  const { mutate, ...rest } = useMutation<
    RemoveEventMutation,
    QueryError,
    RemoveEventMutationVariables
  >({
    ...options,
    mutationFn: (variables) => {
      return gqlClient.request<
        RemoveEventMutation,
        RemoveEventMutationVariables
      >(query, variables);
    },
  });

  const removeEvent = useCallback(
    (
      { eventId }: { eventId: string },
      options?: MutateOptions<
        RemoveEventMutation,
        QueryError,
        RemoveEventMutationVariables
      >
    ) => {
      const input: RemoveEventInput = {
        eventId,
        experienceId,
      };
      mutate({ input }, options);
    },
    [mutate, experienceId]
  );

  return {
    ...rest,
    removeEvent,
  };
};
